import { Component, Input, OnInit } from '@angular/core';
import { ElementType } from 'src/app/_objects/element-type';
import { ReportSummary } from 'src/app/_objects/report-summary';
import { FromToDetail } from 'src/app/_objects/report-template';
import { MousePoint, Notifier, ReportTemplateDesignService } from 'src/app/_services/report-template-design.service';

@Component({
    selector: 'app-report-picture-container',
    templateUrl: './report-picture.component.html',
    styleUrls: ['./report-picture.component.scss'],
    standalone: false
})
export class ReportPictureComponent implements OnInit {

  @Input() public pageKey: string;
  @Input() public pictureKey: string;
  @Input() public elementLocation: string;

  public reportPicture: ReportSummary;
  private notifier: Notifier;
  private inspectionSummaryLinkSize = 4;
  public resizeMarkerSize: number;
  public resizing: boolean;

  constructor(public designService: ReportTemplateDesignService) {
    this.resizeMarkerSize = 36;
    this.resizing = false;
  }

  public ngOnInit() {
    this.notifier = {
      componentName: 'report-picture.component',
      ids: { pageId: this.pageKey, elementId: this.pictureKey }
    };
    this.reportPicture = this.designService.getSummary(this.pictureKey);
    let elementNavigator = this.designService.navigatorElements
    .filter((x) => x.pageKey === this.pageKey)[0]
    .elementsDetails.filter((y) => y.location === this.elementLocation)[0]
      .elements;

     elementNavigator.filter(x => x.elementName === ElementType.Picture).length > 0
      ? elementNavigator.filter(x=>x.elementName == ElementType.Picture)[0].elementData.push({
          id: this.pictureKey,
          page: this.pageKey,
          name: this.reportPicture.getTitle(),
        })
      : elementNavigator.push({
          elementName: ElementType.Picture,
          isVisible: false,
          elementData: [
            {
              id: this.pictureKey,
              page: this.pageKey,
              name: this.reportPicture.getTitle(),
            },
          ],
        });
  }

  public onClick(event: MouseEvent) {
    if (this.reportPicture.isSelectable) {
      console.log('select-picture');
      this.designService.notifySelectElement(this.notifier, this.pictureKey, event.ctrlKey);
      event.stopPropagation();
    }
  }

  public onDragStart(event: DragEvent) {
    // console.log('ReportPicture onDragStart');
    event.dataTransfer.setData('text', this.pictureKey);
    event.dataTransfer.effectAllowed = 'move';

    const fromDetail: FromToDetail = {
      location: this.reportPicture.getLocation(),
      pageId: this.pageKey
    };
    const dragPoint: MousePoint = {
      onElementX: event.offsetX,
      onElementY: event.offsetY
    };

    this.designService.notifyBeginDrag(this.notifier, ElementType.Picture, fromDetail, dragPoint);
  }

  public onDragOver(event: DragEvent) {
    // console.log('ReportPicture onDragOver');
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    event.stopPropagation();
  }

  public onDrop(event: DragEvent) {
    // console.log('ReportPicture onDrop');
    event.preventDefault();
    const template  = {...this.designService.originTemplate};
    template.reportTemplateContent = JSON.stringify(this.designService.getTemplate());
    this.designService.templateForUndo.push(template);
    const dragElemId = event.dataTransfer.getData('text');

    const toDetail: FromToDetail = {
      location: this.reportPicture.getLocation(),
      pageId: this.pageKey
    };
    const dropPoint: MousePoint = {
      onElementX: this.reportPicture.getLeftPx() + event.offsetX,
      onElementY: this.reportPicture.getTopPx() + event.offsetY
    };

    this.designService.notifyDrop(this.notifier, dragElemId, toDetail, dropPoint);

    event.dataTransfer.clearData('text');
    event.stopPropagation();
  }
  
  public getSummaryLinkTop(): string {
    const linkTop = this.reportPicture.getTitleTopNum() - 6;
    return [linkTop, 'mm'].join('');
  }

  public getResizeMarkerSize(): string {
    return this.resizeMarkerSize + 'px';
  }

  public getTitleResizeMarkerLeft(horizontal: string): string {
    switch (horizontal) {
      case 'left':
        return (this.resizeMarkerSize / 2 * -1) + 'px';
      case 'center':
        return ((this.reportPicture.getTitleWidthPx() / 2) - (this.resizeMarkerSize / 2)) + 'px';
      case 'right':
        return (this.reportPicture.getTitleWidthPx() - (this.resizeMarkerSize / 2)) + 'px';
      default:
        return '';
    }
  }

  public getTitleResizeMarkerTop(vertical: string): string {
    switch (vertical) {
      case 'top':
        return (this.resizeMarkerSize / 2 * -1) + 'px';
      case 'middle':
        return ((this.reportPicture.getTitleHeightPx() / 2) - (this.resizeMarkerSize / 2)) + 'px';
      case 'bottom':
        return (this.reportPicture.getTitleHeightPx() - (this.resizeMarkerSize / 2)) + 'px';
      default:
        return '';
    }
  }

  public getTableResizeMarkerLeft(horizontal: string): string {
    switch (horizontal) {
      case 'left':
        return (this.resizeMarkerSize / 2 * -1) + 'px';
      case 'center':
        return ((this.reportPicture.getTableWidthPx() / 2) - (this.resizeMarkerSize / 2)) + 'px';
      case 'right':
        return (this.reportPicture.getTableWidthPx() - (this.resizeMarkerSize / 2)) + 'px';
      default:
        return '';
    }
  }

  public getTableResizeMarkerTop(vertical: string): string {
    switch (vertical) {
      case 'top':
        return (this.resizeMarkerSize / 2 * -1) + 'px';
      case 'middle':
        return ((this.reportPicture.getTableHeightPx() / 2) - (this.resizeMarkerSize / 2)) + 'px';
      case 'bottom':
        return (this.reportPicture.getTableHeightPx() - (this.resizeMarkerSize / 2)) + 'px';
      default:
        return '';
    }
  }

  public onResizeMarkerDown(event: MouseEvent, vertical: string, horizontal: string) {
    event.preventDefault();
    this.resizing = true;
    const beginPoint: MousePoint = {
      onElementX: event.offsetX,
      onElementY: event.offsetY
    };
    this.designService.notifyBeginResize(this.notifier, ElementType.Text, horizontal, vertical, beginPoint);

    event.stopPropagation();
  }

  public onTitleResizing(event: MouseEvent) {
    event.preventDefault();
    if (this.resizing) {
      this.designService.notifyResize(this.notifier, { onElementX: event.offsetX, onElementY: event.offsetY });
    }
    event.stopPropagation();
  }

  public onTableResizing(event: MouseEvent) {
    this.reportPicture.tableResize = true
    event.preventDefault();
    if (this.resizing) {
      this.designService.notifyResize(this.notifier, { onElementX: event.offsetX, onElementY: event.offsetY });
    }
    event.stopPropagation();
  }

  public onResizeMarkerUp(event: MouseEvent) {
    event.preventDefault();
    this.resizing = false;
    this.reportPicture.tableResize = false
    event.stopPropagation();
  }

  public onResizeMarkerLeave(event: MouseEvent) {
    event.preventDefault();
    this.resizing = false;
    this.reportPicture.tableResize = false
    event.stopPropagation();
  }

  replaceLabel(value: string): string {
    return value.replace('objects.', '');
  }
}
